import { Consult } from 'app/models/domain/consult'
import { ConsultDto } from 'app/models/generated'
import { ConsultRepository } from 'app/api/consult/repository/ConsultRepository'
import { AudioRepository } from '../repository/AudioRepository'
import { DiarizationRepository } from 'app/api/consult/repository/DiarizationRepository'
import { TranscriptionRepository } from 'app/api/consult/repository/TranscriptionRepository'
import { EntityRepository } from 'app/api/consult/repository/EntityRepository'
import { ConsultTemplateRepository } from 'app/api/consult/repository/ConsultTemplateRepository'
import { ConsultPatientRepository } from 'app/api/consult/repository/ConsultPatientRepository'
import { logEvent } from 'app/logging/firebase'
import {
    s3StorageRepository,
    StorageRepository,
} from '../repository/StorageRepository'
import { logger } from 'app/logging/Logger'
import { clearRecordingBlob } from 'app/services/recordingBlobCache'
import { store } from 'app/store'
import {
    setUploadProgress,
    setUploadFailure,
    clearUploadProgress,
} from 'app/store/slices/uploadProgressSlice'
import type {
    ConsultCreationResult,
    RelatedRecordFailure,
} from './ConsultCreationResult'

export interface CreateConsultUseCase {
    run(
        consult: Consult,
        /** `undefined` when the recorder produced no final chunk. */
        blob: Blob | undefined,
        onComplete: (consultId: string) => void,
        storageRepository?: StorageRepository
    ): Promise<ConsultCreationResult>

    retryAudioUpload(
        consultId: string,
        audioId: string,
        blob: Blob,
        onComplete: (consultId: string) => void,
        storageRepository?: StorageRepository
    ): Promise<void>
}

export class DefaultCreateConsultUseCase implements CreateConsultUseCase {
    private consultRepository: ConsultRepository
    private audioRepository: AudioRepository
    private diarizationRepository: DiarizationRepository
    private transcriptionRepository: TranscriptionRepository
    private entityRepository: EntityRepository
    private templateRepository: ConsultTemplateRepository
    private patientRepository: ConsultPatientRepository

    constructor(
        createConsultRepository: ConsultRepository,
        audioRepository: AudioRepository,
        diarizationRepository: DiarizationRepository,
        transcriptionRepository: TranscriptionRepository,
        entityRepository: EntityRepository,
        templateRepository: ConsultTemplateRepository,
        patientRepository: ConsultPatientRepository
    ) {
        this.consultRepository = createConsultRepository
        this.audioRepository = audioRepository
        this.diarizationRepository = diarizationRepository
        this.transcriptionRepository = transcriptionRepository
        this.entityRepository = entityRepository
        this.templateRepository = templateRepository
        this.patientRepository = patientRepository
    }

    private logStructuredError(
        error: unknown,
        operation: string,
        consultId?: string,
        audioId?: string | null
    ): void {
        const err = error instanceof Error ? error : new Error(String(error))
        const payload = {
            action: operation,
            metadata: {
                errorMessage: err.message,
                errorCode: err.name || 'UNKNOWN',
                consultId: consultId ?? null,
                audioId: audioId ?? null,
                operation,
                timestamp: new Date().toISOString(),
            },
        }
        logger.error(`[CreateConsult] ${operation} failed`, err, payload)
        logEvent(
            'ERROR',
            operation,
            err.message,
            payload.metadata as Record<string, unknown>
        )
    }

    async run(
        consult: Consult,
        blob: Blob | undefined,
        onComplete: (consultId: string) => void,
        storageRepository: StorageRepository = s3StorageRepository
    ): Promise<ConsultCreationResult> {
        let audioId = storageRepository.audioId
        let createdConsult: ConsultDto | null = null

        logger.debug('[CreateConsult] Starting consult creation', {
            action: 'create_consult_start',
            metadata: {
                audioId,
                hasBlob: !!blob,
                blobSize: blob?.size,
                owner: consult.owner,
                type: consult.templateType,
            },
        })

        // Phase 1: Create ConsultDto — total failure if this fails
        try {
            const consultDto = Consult.buildConsultDto(consult, audioId)
            logger.debug(
                '[CreateConsult] Built ConsultDto, saving to DataStore',
                {
                    action: 'create_consult_saving',
                    metadata: {
                        audioId,
                        hasEntities: !!consultDto.entities,
                        entityCount: consultDto.entities?.length ?? 0,
                    },
                }
            )

            createdConsult = await this.consultRepository.create(consultDto)

            logger.debug('[CreateConsult] DataStore save succeeded', {
                action: 'create_consult_saved',
                metadata: { consultId: createdConsult.id },
            })

            logEvent('INFO', 'create_consult_success', 'Consult created', {
                consultId: createdConsult.id,
            })
        } catch (error) {
            this.logStructuredError(
                error,
                'consult_repository_create',
                undefined,
                audioId
            )

            return {
                status: 'failure',
                error:
                    error instanceof Error ? error : new Error(String(error)),
                audioId,
                retryable: true,
            }
        }

        // Phase 2: Save related records — collect failures
        const failures: RelatedRecordFailure[] = []
        const consultId = createdConsult.id

        // Transcription — with 2 extra retry attempts (3 total)
        try {
            const transcription = consult.transcription

            logEvent(
                'INFO',
                'create_consult_transcription_check',
                'Checking transcription data',
                {
                    consultId,
                    hasTranscription: !!transcription,
                    clientLength:
                        transcription?.clientTranscription?.length || 0,
                }
            )

            if (transcription) {
                let lastError: unknown = null
                const maxAttempts = 3
                for (let attempt = 1; attempt <= maxAttempts; attempt++) {
                    try {
                        await this.transcriptionRepository.create(
                            consultId,
                            transcription.clientTranscription,
                            transcription.cloudTranscription
                        )

                        logEvent(
                            'INFO',
                            'create_consult_transcription_saved',
                            'Transcription saved separately',
                            {
                                consultId,
                                clientLength:
                                    transcription.clientTranscription.length,
                                hasCloud: !!transcription.cloudTranscription,
                            }
                        )
                        lastError = null
                        break
                    } catch (err) {
                        lastError = err
                        if (attempt < maxAttempts) {
                            logger.warn(
                                `[CreateConsult] Transcription save attempt ${attempt}/${maxAttempts} failed, retrying`,
                                {
                                    action: 'transcription_save_retry',
                                    metadata: {
                                        consultId,
                                        attempt,
                                        maxAttempts,
                                        errorMessage: (err as Error)?.message,
                                    },
                                }
                            )
                        }
                    }
                }

                if (lastError) {
                    this.logStructuredError(
                        lastError,
                        'transcription_save',
                        consultId,
                        audioId
                    )
                    failures.push({
                        recordType: 'transcription',
                        error:
                            lastError instanceof Error
                                ? lastError
                                : new Error(String(lastError)),
                        message: `Failed to save transcription after ${maxAttempts} attempts`,
                    })
                }
            } else {
                logEvent(
                    'WARN',
                    'create_consult_no_transcription',
                    'No transcription object returned',
                    { consultId }
                )
            }
        } catch (transcriptionError) {
            this.logStructuredError(
                transcriptionError,
                'transcription_save',
                consultId,
                audioId
            )
            failures.push({
                recordType: 'transcription',
                error:
                    transcriptionError instanceof Error
                        ? transcriptionError
                        : new Error(String(transcriptionError)),
                message: 'Failed to save transcription',
            })
        }

        // Diarization
        const diarization = consult.diarization
        if (diarization && diarization.length > 0) {
            try {
                await this.diarizationRepository.create(consultId, diarization)

                logEvent(
                    'INFO',
                    'create_consult_diarization_saved',
                    'Diarization saved separately',
                    {
                        consultId,
                        segmentCount: diarization.length,
                    }
                )
            } catch (diarizationError) {
                this.logStructuredError(
                    diarizationError,
                    'diarization_save',
                    consultId,
                    audioId
                )
                failures.push({
                    recordType: 'diarization',
                    error:
                        diarizationError instanceof Error
                            ? diarizationError
                            : new Error(String(diarizationError)),
                    message: 'Failed to save diarization',
                })
            }
        }

        // Entities
        const entities = consult.entities
        if (entities && entities.length > 0) {
            try {
                await this.entityRepository.saveEntities(consultId, entities)

                logEvent(
                    'INFO',
                    'create_consult_entities_saved',
                    'Entities saved separately',
                    {
                        consultId,
                        entityCount: entities.length,
                    }
                )
            } catch (entityError) {
                this.logStructuredError(
                    entityError,
                    'entities_save',
                    consultId,
                    audioId
                )
                failures.push({
                    recordType: 'entities',
                    error:
                        entityError instanceof Error
                            ? entityError
                            : new Error(String(entityError)),
                    message: 'Failed to save entities',
                })
            }
        }

        // Template
        const template = consult.template
        if (template) {
            try {
                await this.templateRepository.saveTemplate(consultId, template)

                logEvent(
                    'INFO',
                    'create_consult_template_saved',
                    'Template saved separately',
                    {
                        consultId,
                        templateId: template.id,
                    }
                )
            } catch (templateError) {
                this.logStructuredError(
                    templateError,
                    'template_save',
                    consultId,
                    audioId
                )
                failures.push({
                    recordType: 'template',
                    error:
                        templateError instanceof Error
                            ? templateError
                            : new Error(String(templateError)),
                    message: 'Failed to save template',
                })
            }
        }

        // Patient
        if (consult.patientId) {
            try {
                await this.patientRepository.savePatient(
                    consultId,
                    consult.patientId,
                    consult.attachedPatient,
                    consult.matchedPatients
                )

                logEvent(
                    'INFO',
                    'create_consult_patient_saved',
                    'Patient data saved separately',
                    {
                        consultId,
                        patientId: consult.patientId,
                    }
                )
            } catch (patientError) {
                this.logStructuredError(
                    patientError,
                    'patient_save',
                    consultId,
                    audioId
                )
                failures.push({
                    recordType: 'patient',
                    error:
                        patientError instanceof Error
                            ? patientError
                            : new Error(String(patientError)),
                    message: 'Failed to save patient data',
                })
            }
        }

        // Phase 3: Start audio upload in the background
        // Return immediately so the UI can navigate to History.
        // Upload progress is tracked via Redux (uploadProgressSlice).
        // Upload failures are dispatched to Redux (setUploadFailure).
        // Note: StorageRepository emits progress with audioId, but we need
        // to store it under consultId for the UI to find it. Use the
        // per-consult storageRepository so parallel consults don't clobber it.
        storageRepository.setProgressCallback(
            (_audioId: string, progress: number) => {
                store.dispatch(setUploadProgress({ consultId, progress }))
            }
        )
        if (blob?.size) {
            storageRepository.setExpectedTotalBytes(blob.size)
        }

        // Dispatch initial progress synchronously so the History list
        // sees upload phase on first render (before the async upload starts).
        store.dispatch(setUploadProgress({ consultId, progress: 0.0 }))

        // Fire-and-forget: upload runs in the background. With no audioId (the
        // recording's multipart upload never started) uploadAudio's first
        // AudioDto update could only fail, so take the same failure path.
        // With no blob the S3 upload could only fail too.
        const upload = !audioId
            ? Promise.reject(
                  new Error('No audioId: the recording upload never started')
              )
            : !blob
              ? Promise.reject(
                    new Error('No audio: the recorder produced no recording')
                )
              : this.uploadAudio(audioId, consultId, blob, storageRepository)
        upload
            .then(() => {
                logger.debug(
                    '[CreateConsult] Background audio upload succeeded',
                    {
                        action: 'create_consult_audio_uploaded',
                        metadata: { consultId, audioId },
                    }
                )

                logEvent(
                    'INFO',
                    'create_consult_complete',
                    'Create consult success',
                    {
                        consultId,
                    }
                )

                // Hold at 100% for 2 seconds (matching iOS behaviour) so the
                // user sees "Audio upload complete" before the generation phase.
                logger.debug(
                    '[CreateConsult] Holding upload progress at 1.0 for 2s',
                    {
                        action: 'upload_progress_hold',
                        metadata: { consultId },
                    }
                )
                setTimeout(() => {
                    logger.debug(
                        '[CreateConsult] Clearing upload progress after 2s hold',
                        {
                            action: 'upload_progress_clear',
                            metadata: { consultId },
                        }
                    )
                    store.dispatch(clearUploadProgress(consultId))
                }, 2000)

                // Upload landed: the cached recording is no longer needed.
                clearRecordingBlob(consultId)

                onComplete(consultId)
            })
            .catch((error) => {
                this.logStructuredError(
                    error,
                    'audio_upload',
                    consultId,
                    audioId
                )

                store.dispatch(
                    setUploadFailure({
                        consultId,
                        message:
                            error instanceof Error
                                ? error.message
                                : 'Audio upload failed',
                        isRetryable: true,
                    })
                )
            })

        // Phase 4: Return immediately with 'uploading' status
        if (failures.length > 0) {
            const failedTypes = failures.map((f) => f.recordType).join(', ')
            logger.warn(
                `[CreateConsult] Partial failure: consult ${consultId} created but related records failed: ${failedTypes}`,
                {
                    action: 'create_consult_partial_failure_summary',
                    metadata: {
                        consultId,
                        failedRecordTypes: failures.map((f) => f.recordType),
                        failureCount: failures.length,
                        timestamp: new Date().toISOString(),
                    },
                }
            )

            logEvent(
                'WARN',
                'create_consult_partial_failure',
                `Partial failure: ${failedTypes} failed to save`,
                {
                    consultId,
                    failedRecordTypes: failures.map((f) => f.recordType),
                }
            )
        }

        return {
            status: 'uploading',
            consultId,
            failures,
        }
    }

    async retryAudioUpload(
        consultId: string,
        audioId: string,
        blob: Blob,
        onComplete: (consultId: string) => void,
        storageRepository: StorageRepository = s3StorageRepository
    ): Promise<void> {
        // Guard: a retry with no audio would start a 0-part multipart upload
        // (corrupt/no-op) and can never recover the consult. Fail loudly instead
        // so the UI keeps the retryable failure state (ACA-79).
        if (!blob || blob.size === 0) {
            const err = new Error(
                'No audio available to retry this upload. The original recording is not available in this session.'
            )
            this.logStructuredError(
                err,
                'retry_audio_upload_no_blob',
                consultId,
                audioId
            )
            store.dispatch(
                setUploadFailure({
                    consultId,
                    message:
                        'This recording is no longer available to retry on this device. Please record the consult again.',
                    isRetryable: false,
                })
            )
            throw err
        }

        logger.debug('[CreateConsult] Retry audio upload starting', {
            action: 'retry_audio_upload_start',
            metadata: {
                consultId,
                audioId,
                hasBlob: !!blob,
                blobSize: blob?.size,
                hasConsultId: !!consultId,
                hasAudioId: !!audioId,
            },
        })

        logEvent('INFO', 'retry_audio_upload', 'Retry audio upload', {
            consultId: consultId,
            audioId: audioId,
        })

        try {
            // Set consultId on AudioDto BEFORE S3 retry upload so the Lambda can find it when it fires
            logger.debug(
                '[CreateConsult] Retry setting consultId on AudioDto before S3 upload',
                {
                    action: 'retry_audio_upload_pre_set_consult_id',
                    metadata: { consultId, audioId, blobSize: blob?.size },
                }
            )

            await this.audioRepository.update(audioId, consultId, '', '')

            storageRepository.setProgressCallback(
                (_audioId: string, progress: number) => {
                    store.dispatch(setUploadProgress({ consultId, progress }))
                }
            )
            if (blob?.size) {
                storageRepository.setExpectedTotalBytes(blob.size)
            }

            const { storageKey, url } =
                await storageRepository.retryUploadAudio(blob, audioId)

            logger.debug(
                '[CreateConsult] Retry S3 upload complete, updating AudioDto with full values',
                {
                    action: 'retry_audio_upload_s3_complete',
                    metadata: { consultId, audioId, storageKey, hasUrl: !!url },
                }
            )

            await this.audioRepository.update(
                audioId,
                consultId,
                storageKey,
                url.toString()
            )

            logger.debug('[CreateConsult] Retry audio upload succeeded', {
                action: 'retry_audio_upload_success',
                metadata: { consultId, audioId },
            })

            logEvent(
                'INFO',
                'create_consult_complete',
                'Create consult success',
                { consultId: consultId }
            )
            clearRecordingBlob(consultId)
            onComplete(consultId)
        } catch (error) {
            logger.error('[CreateConsult] Retry FAILED', error as Error, {
                action: 'retry_audio_upload_failed',
                metadata: {
                    errorMessage:
                        error instanceof Error ? error.message : String(error),
                    consultId,
                    audioId,
                },
            })

            logEvent(
                'ERROR',
                'create_consult_failed',
                'Create consult failed',
                { error: error, consultId: consultId }
            )

            store.dispatch(
                setUploadFailure({
                    consultId,
                    message:
                        error instanceof Error
                            ? error.message
                            : 'Audio upload failed',
                    isRetryable: true,
                })
            )
            throw error
        }
    }

    async uploadAudio(
        audioId: string,
        consultId: string,
        blob: Blob,
        storageRepository: StorageRepository = s3StorageRepository
    ): Promise<void> {
        logger.debug(
            '[CreateConsult] uploadAudio setting consultId on AudioDto before S3 upload',
            {
                action: 'upload_audio_pre_set_consult_id',
                metadata: { audioId, consultId, blobSize: blob?.size },
            }
        )

        // Set consultId on AudioDto BEFORE S3 upload so the Lambda can find it when it fires
        await this.audioRepository.update(audioId, consultId, '', '')

        logger.debug('[CreateConsult] uploadAudio starting S3 upload', {
            action: 'upload_audio_start',
            metadata: { audioId, consultId, blobSize: blob?.size },
        })

        const { storageKey, url } = await storageRepository.uploadAudio(blob)

        logger.debug(
            '[CreateConsult] S3 upload complete, updating AudioDto with full values',
            {
                action: 'upload_audio_s3_complete',
                metadata: { audioId, consultId, storageKey, hasUrl: !!url },
            }
        )

        await this.audioRepository.update(
            audioId,
            consultId,
            storageKey,
            url.toString()
        )

        logger.debug('[CreateConsult] AudioDto updated successfully', {
            action: 'upload_audio_dto_updated',
            metadata: { audioId, consultId },
        })
    }
}
